import {
  hasRole,
  promptVariables,
  renderPromptVariables,
  type PromptBlockDto,
  type PromptBlockVersionDto,
  type Role,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { FileText, History, Search } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';

const splitTags = (text: string) =>
  text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

function NewPromptDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('You are a helpful assistant for {{company}}.');
  const [tags, setTags] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<PromptBlockDto>('/prompts', {
        method: 'POST',
        body: { name, description, content, tags: splitTags(tags) },
      }),
    onSuccess: (p) => {
      toast.success(`Prompt ${p.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['prompts', tenantId] });
      setOpen(false);
      void navigate({ to: '/prompts/$promptId', params: { promptId: p.id } });
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New prompt</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a prompt</DialogTitle>
          <DialogDescription>
            Reusable prompt text. <code>{'{{variable}}'}</code> placeholders are filled from the
            request context or endpoint mappings.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="prompt-name">Name</Label>
            <Input
              id="prompt-name"
              className="font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="support-tone"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="prompt-description">Description</Label>
            <Input
              id="prompt-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="prompt-content">Content</Label>
            <Textarea
              id="prompt-content"
              className="min-h-40 font-mono text-xs"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="prompt-tags">Tags</Label>
            <Input
              id="prompt-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="support, tone"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create prompt
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PromptList({ tenantId, role }: { tenantId: string; role: Role }) {
  const [query, setQuery] = useState('');
  const prompts = useQuery({
    queryKey: ['prompts', tenantId],
    queryFn: () => api<{ items: PromptBlockDto[] }>('/prompts'),
  });
  const q = query.trim().toLowerCase();
  const rows = (prompts.data?.items ?? []).filter(
    (p) =>
      !q ||
      p.name.includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.tags.some((t) => t.toLowerCase().includes(q)),
  );
  return (
    <>
      <PageHeader
        title="Prompts"
        description="A versioned library of reusable prompts. Insert them into agent system prompts from the agent editor."
        actions={hasRole(role, 'admin') && <NewPromptDialog tenantId={tenantId} />}
      />
      <div className="grid gap-4">
        <div className="relative max-w-sm">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            type="search"
            className="pl-8"
            placeholder="Search prompts or tags"
            aria-label="Search prompts"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <ErrorAlert error={prompts.error} />
        {prompts.isPending ? (
          <Skeleton className="h-32" />
        ) : rows.length === 0 ? (
          <EmptyState title={q ? 'No prompts match' : 'No prompts yet'}>
            Save instructions you reuse across agents — tone, policies, output formats.
          </EmptyState>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {rows.map((p) => (
              <li key={p.id}>
                <Link
                  to="/prompts/$promptId"
                  params={{ promptId: p.id }}
                  className="block h-full rounded-xl focus-visible:ring-2 focus-visible:outline-none"
                >
                  <Card className="hover:bg-muted/40 h-full transition-colors">
                    <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                      <div className="grid gap-1">
                        <CardTitle className="flex items-center gap-2 font-mono text-base">
                          <FileText className="size-4" aria-hidden />
                          {p.name}
                        </CardTitle>
                        <CardDescription>{p.description || 'No description'}</CardDescription>
                      </div>
                      <Badge variant="outline">v{p.version}</Badge>
                    </CardHeader>
                    <CardContent className="grid gap-2 text-xs">
                      <p className="text-muted-foreground line-clamp-2 font-mono whitespace-pre-wrap">
                        {p.content}
                      </p>
                      <div className="flex flex-wrap items-center gap-1">
                        {p.variables.map((v) => (
                          <Badge key={v} variant="secondary" className="font-mono font-normal">
                            {`{{${v}}}`}
                          </Badge>
                        ))}
                        {p.tags.map((t) => (
                          <Badge key={t} variant="outline" className="font-normal">
                            {t}
                          </Badge>
                        ))}
                        <span className="text-muted-foreground ml-auto">
                          Updated {formatRelative(p.updatedAt)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export function PromptsPage() {
  return (
    <RequireTenant>
      {(tenant) => <PromptList tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}

// --- Detail ---

function Preview({ content }: { content: string }) {
  const variables = promptVariables(content);
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <div className="grid gap-3">
      {variables.length === 0 ? (
        <p className="text-muted-foreground text-sm">This prompt has no variables.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {variables.map((v) => (
            <div key={v} className="grid gap-1">
              <Label htmlFor={`var-${v}`} className="font-mono text-xs">
                {v}
              </Label>
              <Input
                id={`var-${v}`}
                value={values[v] ?? ''}
                onChange={(e) => setValues((all) => ({ ...all, [v]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}
      <pre
        className="bg-muted max-h-96 overflow-auto rounded-md p-3 text-xs whitespace-pre-wrap"
        aria-label="Rendered prompt"
      >
        {renderPromptVariables(
          content,
          Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '')),
        )}
      </pre>
    </div>
  );
}

function PromptEditor({
  tenantId,
  role,
  promptId,
}: {
  tenantId: string;
  role: Role;
  promptId: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasRole(role, 'admin');
  const detail = useQuery({
    queryKey: ['prompt', tenantId, promptId],
    queryFn: () =>
      api<{ prompt: PromptBlockDto; versions: PromptBlockVersionDto[] }>(`/prompts/${promptId}`),
  });
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const prompt = detail.data?.prompt;
  useEffect(() => {
    if (!prompt) return;
    setContent(prompt.content);
    setDescription(prompt.description);
    setTags(prompt.tags.join(', '));
  }, [prompt]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['prompt', tenantId, promptId] });
    void queryClient.invalidateQueries({ queryKey: ['prompts', tenantId] });
  };
  const save = useMutation({
    mutationFn: () =>
      api<PromptBlockDto>(`/prompts/${promptId}`, {
        method: 'PATCH',
        body: { content, description, tags: splitTags(tags) },
      }),
    onSuccess: (p) => {
      toast.success(p.version !== prompt?.version ? `Saved as version ${p.version}` : 'Saved');
      refresh();
    },
  });
  const restore = useMutation({
    mutationFn: (version: number) =>
      api<PromptBlockDto>(`/prompts/${promptId}/restore`, { method: 'POST', body: { version } }),
    onSuccess: (p) => {
      toast.success(`Restored as version ${p.version}`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/prompts/${promptId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Prompt deleted');
      void queryClient.invalidateQueries({ queryKey: ['prompts', tenantId] });
      void navigate({ to: '/prompts' });
    },
  });
  const [deleting, setDeleting] = useState(false);

  if (detail.error) return <ErrorAlert error={detail.error} />;
  if (!prompt) return <Skeleton className="h-64" />;
  const dirty =
    content !== prompt.content ||
    description !== prompt.description ||
    tags !== prompt.tags.join(', ');

  return (
    <>
      <PageHeader
        title={prompt.name}
        description={`Version ${prompt.version} · updated ${formatDateTime(prompt.updatedAt)}`}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/prompts">All prompts</Link>
            </Button>
            {canEdit && (
              <>
                <Button variant="destructive" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
                <ConfirmDeleteDialog
                  open={deleting}
                  onOpenChange={setDeleting}
                  title={`Delete ${prompt.name}?`}
                  description="The prompt and all its versions are deleted. Agents that use it keep their copy of the text."
                  onConfirm={() => remove.mutateAsync()}
                />
                <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="grid content-start gap-4">
          <ErrorAlert error={save.error} />
          <Card>
            <CardContent className="grid gap-4 pt-6">
              <div className="grid gap-1.5">
                <Label htmlFor="prompt-edit-description">Description</Label>
                <Input
                  id="prompt-edit-description"
                  value={description}
                  disabled={!canEdit}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prompt-edit-tags">Tags</Label>
                <Input
                  id="prompt-edit-tags"
                  value={tags}
                  disabled={!canEdit}
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
          <Tabs defaultValue="edit">
            <TabsList>
              <TabsTrigger value="edit">Content</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="edit">
              <Textarea
                aria-label="Prompt content"
                className="min-h-96 font-mono text-xs"
                value={content}
                disabled={!canEdit}
                onChange={(e) => setContent(e.target.value)}
              />
              <p className="text-muted-foreground mt-2 text-xs">
                {content.length.toLocaleString()} characters · {promptVariables(content).length}{' '}
                variables. Saving changed content creates a new version.
              </p>
            </TabsContent>
            <TabsContent value="preview">
              <Preview content={content} />
            </TabsContent>
          </Tabs>
        </div>
        <Card className="self-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="size-4" aria-hidden /> Versions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="grid gap-2" aria-label="Prompt versions">
              {detail.data!.versions.map((v) => (
                <li key={v.id} className="grid gap-1 rounded-md border p-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      v{v.version}
                      {v.version === prompt.version && (
                        <Badge variant="secondary" className="ml-1.5">
                          current
                        </Badge>
                      )}
                    </span>
                    <span className="text-muted-foreground">{formatRelative(v.createdAt)}</span>
                  </div>
                  <p className="text-muted-foreground line-clamp-2 font-mono">{v.content}</p>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => setContent(v.content)}
                    >
                      Load
                    </Button>
                    {canEdit && v.version !== prompt.version && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(v.version)}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export function PromptDetailPage() {
  const { promptId } = useParams({ from: '/app/prompts/$promptId' });
  return (
    <RequireTenant>
      {(tenant) => <PromptEditor tenantId={tenant.id} role={tenant.role} promptId={promptId} />}
    </RequireTenant>
  );
}
